import * as React from 'react';
import { TranslateFn, AnsViewComp } from '@wix/answers-app-core';
import { ChatTimelineItem, UserTimelineType, ChatTimelineMessage, ChatTimelineTimestamp,
	ChatTyping, ChatGroupPositionType, ChatEmailForm, ChatTimelineEvent } from './types';
import { TimelineBubble } from './timeline-bubble';
import { Icon, DeprecatedText, DeprecatedButton, SpinnerLoader, UserAvatar,
pdfFile, docFile, xlsFile, download, thinArrowDown } from '@wix/answers-ui-libby';
import { ChatMessageType, ActivityEventType } from '@wix/answers-api';
import { ImageTimeline } from './image-timeline';
import { getShortenedNumber, getContentType, AttachmentTypes } from '../utils';
import { noop, classNames } from '@wix/answers-lib';
import { EmailForm } from './email-form';
import { sanitizeExceptWidgetSdk } from '@wix/answers-widget-viewer';

export const chatTimelineKey = 'chat-timeline';
export const SCROLL_BOTTOM_THRESHOLD = 50; // max distance for auto-scroll on new message

export type ChatTimelineProps = {
	items: ChatTimelineItem[];
	onSubmitEmail: (email: string) => Promise<void>;
	isMobile?: boolean;
	isEmailOptional: boolean;
	onResendMessage: (message: ChatTimelineMessage) => Promise<void>;
	loading?: boolean;
};

const translationPrefix = `widget-ng.chat.chat-room.${chatTimelineKey}`;

export type ChatTimelineState = {
	newItemsCount: number;
	scrollAtBottom: boolean;
};

export class ChatTimeline extends AnsViewComp<ChatTimelineProps, ChatTimelineState> {

	state: ChatTimelineState = {
		newItemsCount: 0,
		scrollAtBottom: false,
	};

	private messageListRef: any;

	tp: TranslateFn = (key, params) => {
		return this.props.t(`${translationPrefix}.${key}`, params);
	}

	getContentTypeIcon = (contentType: string) => {
		const attachmentType = getContentType(contentType);
		switch (attachmentType) {
			case AttachmentTypes.PDF:
				return <Icon icon={pdfFile}/>;
				break;
			case AttachmentTypes.DOC:
				return <Icon icon={docFile}/>;
				break;
			case AttachmentTypes.XLS:
				return <Icon icon={xlsFile}/>;
				break;
			default:
				return null;
				break;
		}
	}

	renderFile = (item: ChatTimelineMessage) => {
		const data = item.chatMessageData;
		const file = data ? data.attachments[0] : null;
		if (!file) {
			return null;
		}
		const contentType = getContentType(file.contentType);
		const fileIcon = this.getContentTypeIcon(contentType);
		const size =  file.sizeBytes;
		const convertSize = getShortenedNumber(size);
		const isUser = item.chatMessageType === ChatMessageType.USER;
		const fileDetails = (
			<div className='file-details'>
				<span className={classNames('file-name', {user: isUser })}> {file.name} </span>
				<span className={classNames('file-size', {user: isUser})}> {convertSize} </span>
			</div>
		);
		const downloadIcon = (
			<div className={classNames('download-icon', {user: isUser})}>
				<Icon icon={download}/>
			</div>
		);

		return (
			<div className='file'>
				<div className='file-info'>
					{fileIcon}
					<a href={file.url} target='_blank'> {fileDetails} </a>
				</div>
				<a href={file.url} download={file.name} target='_blank'>
					{downloadIcon}
				</a>
			</div>
		);
	}

	getTimelineBubbleContent = (item: ChatTimelineMessage) => {
		const data = item.chatMessageData;
		if (data) {
			const hasAttachment = data.attachments.length > 0;
			const attachmentType = getContentType(data.attachments[0].contentType);
			if (hasAttachment) {
				return (
					attachmentType === AttachmentTypes.IMAGE
					? <ImageTimeline item={item} />
					: this.renderFile(item)
				);
			}
		}

		const matchImageAndCaptureSrcRegex = /<img src=\"([^"]*)\"/g;
		const matchLink = /<a( [^>]*>)/g;

		const addOnClickToImage = (match: string, src: string) =>
			`${match} onclick="parent.postMessage({imageSrc: '${src}'}, '*')"`;

		const openLinkInNewTab = (_: string, link: string) =>
			`<a target="_blank"${link}`;

		const cleanText = sanitizeExceptWidgetSdk(item.text);
		const textWithImageClickAndLinkInNewTab = cleanText
			.replace(matchImageAndCaptureSrcRegex, addOnClickToImage)
			.replace(matchLink, openLinkInNewTab);
		return (
			<div className='message-text' dangerouslySetInnerHTML={{ __html: textWithImageClickAndLinkInNewTab }} />
		);
	}

	renderTimelineBubble = (item: ChatTimelineMessage, i: number) => {
		const onRetry = () => {
			return this.props.onResendMessage(item);
		};
		const content = this.getTimelineBubbleContent(item);
		return (
		<TimelineBubble
			key={i}
			bubbleType={item.chatMessageType}
			timestamp={item.creationDate}
			user={item.user}
			t={this.props.t}
			groupPosition={item.groupPosition}
			status={item.status}
			onRetry={onRetry}
			disableTooltip={this.props.isMobile}
		>
			{content}
		</TimelineBubble>
		);
	}

	renderTyping = (item: ChatTyping, i: number) => {
		return (
			<TimelineBubble
				key={i}
				bubbleType={ChatMessageType.AGENT}
				user={item.user}
				groupPosition={ChatGroupPositionType.ONLY}
				t={this.props.t}
				timestamp={Date.now()}
				onRetry={noop}
				disableTooltip={this.props.isMobile}
			>
				<SpinnerLoader color='#577083'/>
			</TimelineBubble>
		);
	}

	renderEmailForm = (item: ChatEmailForm, i: number) => {
		return (
			<TimelineBubble
				key={i}
				bubbleType={ChatMessageType.AGENT}
				timestamp={item.creationDate}
				user={item.user}
				t={this.props.t}
				groupPosition={item.groupPosition}
				onRetry={noop}
				disableTooltip={this.props.isMobile}
			>
				<EmailForm
					optional={this.props.isEmailOptional}
					email={item.email}
					onSubmit={this.props.onSubmitEmail}
					t={this.props.t}
				/>
			</TimelineBubble>
		);
	}

	getEventContent = (item: ChatTimelineEvent) => {
		const name = item.user.firstName;
		switch (item.eventType) {
			case ActivityEventType.AGENT_JOINED_CHAT:
				return this.tp('event.agent-joined', { name });
			case ActivityEventType.AGENT_LEFT_CHAT:
				return this.tp('event.agent-left', { name });
			case ActivityEventType.AGENT_ENDED_CHAT:
				return this.tp('event.agent-ended', { name });
			default:
				return '';
		}
	}

	renderEvent = (item: ChatTimelineEvent, i: number) => {
		return (
			<div className='timeline-event' key={i}>
				<UserAvatar className='agent-avatar' size='xsmall' user={item.user} isAgent={true}/>
				<DeprecatedText type='t4d' className='event-content'> {this.getEventContent(item)} </DeprecatedText>
			</div>
		);
	}

	renderTimeLineItem = (item: ChatTimelineItem, i: number) => {
		switch (item.userTimelineType) {
			case UserTimelineType.REPLY:
				return this.renderTimelineBubble(item, i);
			case UserTimelineType.TIMESTAMP:
				return this.renderTimestamp(item, i);
			case UserTimelineType.TYPING:
				return this.renderTyping(item, i);
			case UserTimelineType.EMAIL_FORM:
				return this.renderEmailForm(item, i);
			case UserTimelineType.EVENT:
				return this.renderEvent(item, i);
			default:
				return null;
		}
	}

	renderTimestamp = (item: ChatTimelineTimestamp, i: number) => (
		<div className='timestamp' key={i}>
			<DeprecatedText type='t4d'> {item.time}</DeprecatedText>
		</div>
	)

	renderNewMessagesBubble = (count: number) => (
		<div className={'new-messages-bubble'}>
			<DeprecatedButton onClick={this.scrollToBottom} className={'new-message-button'}>
				{count > 1 ? this.tp('new-messages-multi', { count }) : this.tp('new-message', { count })}
				<Icon className={'new-messages-icon'} icon={thinArrowDown} />
			</DeprecatedButton>
		</div>
	)

	renderTypingBubble = () => (
		<div className={'new-messages-bubble'}>
			<DeprecatedButton onClick={this.scrollToBottom} className={'new-message-button'}>
				{this.tp('typing')}
				<div className='dots'>
					<div className='dot'>.</div>
					<div className='dot'>.</div>
					<div className='dot'>.</div>
				</div>
				<Icon className={'new-messages-icon'} icon={thinArrowDown} />
			</DeprecatedButton>
		</div>
	)

	setChatTimelineRef = (node: HTMLDivElement) => {
		this.messageListRef = node;
	}

	scrollToBottom = () => {
		const { messageListRef } = this;
		const maxScrollTop = (messageListRef.scrollHeight - messageListRef.clientHeight);
		messageListRef.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
		this.setState({ newItemsCount: 0 });
	}

	isScrolledAtBottom = () => {
		const { messageListRef } = this;
		if (!messageListRef) {
			return false;
		}
		const scrollPos = messageListRef.scrollTop;
		const scrollBottom = (messageListRef.scrollHeight - messageListRef.clientHeight);
		const scrollAtBottom = (scrollBottom <= 0)
			|| (scrollBottom - scrollPos <= SCROLL_BOTTOM_THRESHOLD)
			|| (scrollPos === scrollBottom);
		return scrollAtBottom;
	}

	handleScroll = () => {
		const scrollAtBottom = this.isScrolledAtBottom();
		if (scrollAtBottom) {
			this.setState({ newItemsCount: 0 });
		}
	}

	componentWillReceiveProps (nextProps: ChatTimelineProps) {
		const didReceivedNewItem = nextProps.items.length !== this.props.items.length;
		if (didReceivedNewItem) {
			const scrollAtBottom = this.isScrolledAtBottom();
			this.setState({ scrollAtBottom });
		}
	}

	componentDidUpdate (prevProps: ChatTimelineProps, prevState: ChatTimelineState) {
		const newItems = this.props.items;
		const didReceivedNewItem = this.props.items.length > prevProps.items.length;
		if (didReceivedNewItem) {
			const newItemsCount = prevState.newItemsCount
				+ newItems.filter((item) => item.userTimelineType !== UserTimelineType.TYPING).length
				- prevProps.items.filter((item) => item.userTimelineType !== UserTimelineType.TYPING).length;
			const lastMessage = newItems[newItems.length - 1];
			this.setState({ newItemsCount });
			const isLastMessageFromSelf = lastMessage
				&& lastMessage.userTimelineType === UserTimelineType.REPLY
				&& lastMessage.chatMessageType === ChatMessageType.USER;
			if (this.state.scrollAtBottom || isLastMessageFromSelf) {
				this.scrollToBottom();
			}
		}
	}

	renderLoader = () => (
		<div className='timeline-loader'>
			{this.tp('loading')}
			<div className='dots-appearing'>
				<div className='dot'>.</div>
				<div className='dot'>.</div>
				<div className='dot'>.</div>
			</div>
		</div>
	)

	render () {
		const { props, state } = this;
		const timelineItems = props.items.map((item, i) => (
			this.renderTimeLineItem(item, i)
		));
		const {newItemsCount} = state;

		const lastItem = props.items[props.items.length - 1];
		const isTyping = lastItem && lastItem.userTimelineType === UserTimelineType.TYPING;
		const shouldShowTyping = isTyping && !this.isScrolledAtBottom();

		return (
			<div className={classNames(chatTimelineKey, {loading: props.loading})} >
				{props.loading ? this.renderLoader() : null}
				<div className='timeline-items-wrapper' ref={this.setChatTimelineRef} onScroll={this.handleScroll}>
					{timelineItems}
				</div>
				{shouldShowTyping ? this.renderTypingBubble() : (!!newItemsCount && this.renderNewMessagesBubble(newItemsCount))}
			</div>

		);
	}
}
